import { useCallback, useEffect, useState } from 'react';

const STORAGE_PREFIX = 'tixologist:pinned-actions:event-';
const UPDATE_EVENT = 'tixologist:pinned-actions-updated';

// Max quick-access pins kept at once; pinning a 4th evicts the oldest (FIFO). Zone-local copy of the
// green-zone cap in features/PunchupPages/CustomFieldsResponse/hooks/usePinnedActions.ts (MAX_PINNED_ACTIONS).
const MAX_PINNED_ACTIONS = 3;

const storageKey = (eventId?: string) => `${STORAGE_PREFIX}${eventId ?? 'global'}`;

const readSet = (key: string): Set<string> => {
	if (typeof window === 'undefined') return new Set();
	try {
		const raw = window.localStorage.getItem(key);
		if (!raw) return new Set();
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return new Set();
		return new Set(parsed.filter((x): x is string => typeof x === 'string'));
	} catch {
		return new Set();
	}
};

const writeSet = (key: string, value: Set<string>) => {
	if (typeof window === 'undefined') return;
	try {
		window.localStorage.setItem(key, JSON.stringify(Array.from(value)));
		window.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail: { key } }));
	} catch {
		// localStorage unavailable — pin state degrades to per-component memory
	}
};

export const usePinnedActions = (eventId?: string) => {
	const key = storageKey(eventId);
	const [pinned, setPinned] = useState<Set<string>>(() => readSet(key));

	useEffect(() => {
		setPinned(readSet(key));
	}, [key]);

	useEffect(() => {
		const onStorage = (e: StorageEvent) => {
			if (e.key === key) setPinned(readSet(key));
		};
		const onLocal = (e: Event) => {
			const detail = (e as CustomEvent).detail;
			if (detail?.key === key) setPinned(readSet(key));
		};
		window.addEventListener('storage', onStorage);
		window.addEventListener(UPDATE_EVENT, onLocal);
		return () => {
			window.removeEventListener('storage', onStorage);
			window.removeEventListener(UPDATE_EVENT, onLocal);
		};
	}, [key]);

	const togglePin = useCallback(
		(actionId: string) => {
			const next = new Set(readSet(key));
			if (next.has(actionId)) {
				next.delete(actionId);
			} else {
				next.add(actionId);
				// Cap the pinned set: pinning beyond MAX_PINNED_ACTIONS evicts the OLDEST still-pinned action
				// (FIFO — a Set preserves insertion order). The quick buttons render in menu order (AttendeeActions
				// filters visibleActions by isPinned), so eviction changes which actions show, not their order.
				while (next.size > MAX_PINNED_ACTIONS) {
					const oldest = next.values().next().value;
					if (oldest === undefined) break;
					next.delete(oldest);
				}
			}
			writeSet(key, next);
			setPinned(next);
		},
		[key]
	);

	const isPinned = useCallback((actionId: string) => pinned.has(actionId), [pinned]);

	return { pinnedIds: pinned, isPinned, togglePin };
};
